import { retryWithBackoff } from "../../../utils";
import type {
  ConnectionState,
  StreamRequest,
  TTSClient,
  WordTimestamp,
} from "../../client";
import type { ElevenLabsTTSConfig } from "./client";
import { alignmentToWordTimestamps, decodePcm16ToFloat32 } from "./utils";
import type { TokenMintAuth } from "@/lib/interview/tokenMintAuth";
import {
  createRealtimeConnectionLog,
  getCloseEventDetails,
  type RealtimeCloseRequest,
  type RealtimeConnectionLog,
} from "@/lib/realtimeConnectionLog";
import { logger } from "@/utils/logging/isomorphic";
import { postJson } from "@/utils/routeUtils";

// v4 Turbo is served only by the Text to Dialogue websocket: no context ids, one
// voice per socket, and a flush ends the turn (resetting prosody).
export const ELEVENLABS_DIALOGUE_MODEL_ID = "eleven_v4_turbo";

// "full_response" holds the reply until the LLM finishes, so the model plans its
// delivery with the whole reply in view. "streamed" sends each delta as it
// arrives, so speech starts sooner with only a few words of lookahead.
export type DialogueTextRelease = "full_response" | "streamed";

// Prepended to every text sent for generation. v4 Turbo reads bracketed tags as
// delivery cues and does not speak them.
export const EMOTION_TAGS = "[curious] [semi-fast]";

// A tag can arrive split across alignment chunks, e.g. "[cur" then "ious]".
const isEmotionTagPiece = (word: string) =>
  word.startsWith("[") || word.endsWith("]");

const SAMPLE_RATE = 44100;
// Under both the server's 20s inactivity close and our 10s idle disconnect.
const KEEP_ALIVE_INTERVAL_MS = 5_000;
// Text that stopped arriving means the reply was abandoned (muted, unmounted), so let the socket idle out.
const KEEP_ALIVE_MAX_TEXT_GAP_MS = 20_000;

interface DialogueAlignment {
  chars: string[];
  char_start_times_ms: number[];
  char_durations_ms: number[];
}

interface DialogueServerMessage {
  audio?: string;
  alignment?: DialogueAlignment | null;
  is_final_audio_for_turn?: boolean;
  error?: string;
  message?: string;
}

// One spoken item. Text waits in `unsentText` until the release strategy sends
// it; the flush that ends the turn goes out once the reply is complete.
interface Turn {
  contextId: string;
  unsentText: string;
  isTextComplete: boolean;
  isSocketReady: boolean;
  hasSentText: boolean;
  isFlushed: boolean;
  isAudioDone: boolean;
  receivedSamples: number;
  lastTextAt: number;
}

export const createElevenLabsDialogueTTSClient = ({
  getConfig,
  getAuth,
  textRelease,
  onClose,
  onMessage,
  onConnect,
}: {
  getConfig: () => ElevenLabsTTSConfig;
  getAuth: () => TokenMintAuth | undefined;
  textRelease: DialogueTextRelease;
  onClose: () => void;
  onMessage: () => void;
  onConnect: () => void;
}): TTSClient => {
  let connectionState: ConnectionState = "disconnected";
  let connectingPromise: Promise<void> | null = null;
  let ws: WebSocket | null = null;
  let connectionLog: RealtimeConnectionLog | null = null;
  let destroyed = false;
  let turn: Turn | null = null;
  let currentOnTimestamps: ((timestamps: WordTimestamp[]) => void) | null =
    null;

  let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  let keepAliveTurn: Turn | null = null;

  let audioQueue: Float32Array[] = [];
  let queueResolve: (() => void) | null = null;
  let streamEnded = false;

  const _resetQueue = () => {
    audioQueue = [];
    queueResolve = null;
    streamEnded = false;
  };

  const _enqueueAudio = (chunk: Float32Array) => {
    audioQueue.push(chunk);
    queueResolve?.();
    queueResolve = null;
  };

  const _signalEnd = () => {
    streamEnded = true;
    queueResolve?.();
    queueResolve = null;
  };

  const _fetchToken = async (): Promise<string> => {
    const { success, data, error } = await postJson(
      "/api/chat/initialize-tts-elevenlabs",
      { ...getAuth(), tokenType: "ttd_websocket" }
    );
    if (!success || data.token == null) {
      throw new Error(error || "Failed to get ElevenLabs dialogue token");
    }
    return data.token;
  };

  const _handleAudio = (activeTurn: Turn, data: DialogueServerMessage) => {
    if (!data.audio) return;
    const samples = decodePcm16ToFloat32(data.audio);
    // Alignment times restart at zero on every chunk; shift them onto the turn.
    if (data.alignment) {
      const offsetMs = (activeTurn.receivedSamples / SAMPLE_RATE) * 1000;
      // Tags come back in the alignment but are not spoken words.
      const wordTimestamps = alignmentToWordTimestamps({
        chars: data.alignment.chars,
        charStartTimesMs: data.alignment.char_start_times_ms.map(
          (startMs) => startMs + offsetMs
        ),
        charDurationsMs: data.alignment.char_durations_ms,
      }).filter((timestamp) => !isEmotionTagPiece(timestamp.word));
      if (wordTimestamps.length > 0) {
        currentOnTimestamps?.(wordTimestamps);
      }
    }
    activeTurn.receivedSamples += samples.length;
    _enqueueAudio(samples);
  };

  const _openWebSocket = ({
    token,
    log,
    attempt,
  }: {
    token: string;
    log: RealtimeConnectionLog;
    attempt: number;
  }): Promise<void> => {
    const config = getConfig();
    return new Promise<void>((resolve, reject) => {
      const url = new URL(
        "wss://api.elevenlabs.io/v1/text-to-dialogue/stream-input"
      );
      url.searchParams.set("model_id", config.modelId);
      // zero retention mode: required for compliance
      url.searchParams.set("enable_logging", "false");
      url.searchParams.set("output_format", "pcm_44100");
      url.searchParams.set("sync_alignment", "true");
      url.searchParams.set("single_use_token", token);

      const socket = new WebSocket(url.toString());
      ws = socket;
      let settled = false;

      socket.onopen = () => {
        if (ws !== socket) {
          socket.close();
          if (!settled) {
            settled = true;
            reject(new Error("Connection superseded"));
          }
          return;
        }
        // No voice_settings, so the voice's own library defaults apply.
        socket.send(JSON.stringify({ voices: [config.voiceId] }));
        settled = true;
        log.recordConnected();
        resolve();
      };

      socket.onmessage = (event: MessageEvent) => {
        if (ws !== socket) return;
        const data = JSON.parse(event.data as string) as DialogueServerMessage;
        if (data.error) {
          logger.captureException(
            new Error(
              `ElevenLabs dialogue error: ${data.error}: ${data.message ?? ""}`
            )
          );
          // The server closes the socket next; ending the turn now keeps that close from being reported again.
          const failedTurn = turn;
          if (failedTurn && !failedTurn.isAudioDone) {
            failedTurn.isFlushed = true;
            failedTurn.isAudioDone = true;
            _signalEnd();
          }
          return;
        }
        const activeTurn = turn;
        if (!activeTurn || activeTurn.isAudioDone) return;
        if (data.audio) {
          _handleAudio(activeTurn, data);
          onMessage();
        }
        if (data.is_final_audio_for_turn) {
          activeTurn.isAudioDone = true;
          _signalEnd();
        }
      };

      socket.onerror = () => {
        if (!settled) {
          settled = true;
          reject(new Error("ElevenLabs dialogue WebSocket connection failed"));
        }
      };

      socket.onclose = (event: CloseEvent) => {
        log.recordClosed({ attempt, details: getCloseEventDetails(event) });
        if (ws === socket) {
          ws = null;
          _stopKeepAlive();
          const activeTurn = turn;
          if (activeTurn && !activeTurn.isAudioDone) {
            logger.captureException(
              new Error(
                `ElevenLabs dialogue socket closed mid-turn: ${event.code} ${event.reason}`
              )
            );
            // Stops a reconnect from sending this reply when nothing would play it.
            activeTurn.isFlushed = true;
            activeTurn.isAudioDone = true;
          }
          _signalEnd();
          connectionState = "disconnected";
          connectingPromise = null;
          onClose();
        }
        if (!settled) {
          settled = true;
          reject(
            new Error("ElevenLabs dialogue WebSocket closed before connecting")
          );
        }
      };
    });
  };

  const connect = (): Promise<void> => {
    destroyed = false;

    if (connectionState === "ready" && ws) {
      onConnect();
      return Promise.resolve();
    }

    if (connectingPromise) {
      return connectingPromise;
    }

    const log = createRealtimeConnectionLog({
      path: "tts",
      provider: "elevenlabs",
    });
    connectionLog = log;
    connectingPromise = (async () => {
      try {
        connectionState = "connecting";

        await retryWithBackoff({
          fn: async () => {
            const attempt = log.recordAttempt();
            connectionState = "fetching-token";
            const token = await _fetchToken();
            connectionState = "connecting";
            await _openWebSocket({ token, log, attempt });
          },
          backoffs: [1000, 2000, 4000],
          shouldAbort: () => destroyed,
          onRetry: (attempt, delay) => {
            console.log(
              `[ElevenLabs dialogue TTS] Retrying connection in ${delay}ms (attempt ${attempt + 1})`
            );
          },
        });

        if (!ws) {
          throw new Error("Failed to establish WebSocket connection");
        }

        connectionState = "ready";
        onConnect();
      } catch (error) {
        if (destroyed) return;
        log.recordConnectFailed();
        logger.captureException(error);
        connectionState = "failed";
        ws = null;
        connectingPromise = null;
        throw error;
      }
    })();

    return connectingPromise;
  };

  const _stopKeepAlive = () => {
    if (keepAliveTimer) {
      clearInterval(keepAliveTimer);
      keepAliveTimer = null;
    }
    keepAliveTurn = null;
  };

  // The server closes a socket that receives nothing for 20s, and a slow LLM can
  // leave a turn unflushed for longer, so ping while the reply is still being written.
  const _keepAliveUntilFlushed = (activeTurn: Turn) => {
    _stopKeepAlive();
    const ping = () => {
      const socket = ws;
      const isWriterActive =
        Date.now() - activeTurn.lastTextAt < KEEP_ALIVE_MAX_TEXT_GAP_MS;
      const isStillWaiting =
        turn === activeTurn &&
        !activeTurn.isFlushed &&
        isWriterActive &&
        socket?.readyState === WebSocket.OPEN;
      if (!isStillWaiting || !socket) {
        _stopKeepAlive();
        return;
      }
      socket.send(JSON.stringify({ keep_alive: true }));
      // Also holds off the idle disconnect, which only sees server messages.
      onMessage();
    };
    keepAliveTurn = activeTurn;
    keepAliveTimer = setInterval(ping, KEEP_ALIVE_INTERVAL_MS);
    ping();
  };

  // Also restarts pinging when text resumes after a pause stopped it.
  const _ensureKeepAlive = (activeTurn: Turn) => {
    const isWaitingOnReadySocket =
      activeTurn.isSocketReady && !activeTurn.isFlushed;
    if (keepAliveTurn === activeTurn || !isWaitingOnReadySocket) return;
    _keepAliveUntilFlushed(activeTurn);
  };

  // Runs after every delta and once the socket opens. Sends whatever text the
  // release strategy allows, plus the flush once the reply is complete.
  const _sendReleasableText = () => {
    const activeTurn = turn;
    const socket = ws;
    if (!activeTurn || !socket) return;
    const canSend =
      activeTurn.isSocketReady &&
      !activeTurn.isFlushed &&
      socket.readyState === WebSocket.OPEN;
    if (!canSend) return;

    const isTextReleasable =
      textRelease === "streamed" || activeTurn.isTextComplete;
    // Whitespace-only text waits for the next word so words are not joined.
    const textToSend =
      isTextReleasable && activeTurn.unsentText.trim()
        ? activeTurn.unsentText
        : null;
    const shouldFlush = activeTurn.isTextComplete;
    if (textToSend === null && !shouldFlush) return;

    if (shouldFlush) {
      activeTurn.isFlushed = true;
      _stopKeepAlive();
    }
    if (textToSend === null && !activeTurn.hasSentText) {
      // Nothing to say, so no turn-final message will come back.
      activeTurn.isAudioDone = true;
      _signalEnd();
      return;
    }

    const isFirstInput = !activeTurn.hasSentText;
    const inputs =
      textToSend === null
        ? []
        : [
            {
              text: isFirstInput ? `${EMOTION_TAGS} ${textToSend}` : textToSend,
              voice_id: getConfig().voiceId,
              new_turn: isFirstInput,
            },
          ];
    if (textToSend !== null) {
      activeTurn.unsentText = "";
      activeTurn.hasSentText = true;
    }
    // flush ends the turn: the server voices all of it, then sends is_final_audio_for_turn.
    socket.send(
      JSON.stringify({
        ...(inputs.length > 0 ? { inputs } : {}),
        ...(shouldFlush ? { flush: true } : {}),
      })
    );
  };

  const _appendText = (activeTurn: Turn, text: string, isLast: boolean) => {
    activeTurn.unsentText += text;
    activeTurn.lastTextAt = Date.now();
    if (isLast) {
      activeTurn.isTextComplete = true;
    }
    _sendReleasableText();
    _ensureKeepAlive(activeTurn);
  };

  const stream = ({
    text,
    contextId,
    maybeHasMore,
    onTimestamps,
  }: StreamRequest): AsyncIterable<Float32Array> => {
    async function* generate(): AsyncGenerator<Float32Array> {
      // Deltas for the turn already speaking: queue the text, play nothing here.
      if (turn?.contextId === contextId) {
        _appendText(turn, text, !maybeHasMore);
        return;
      }

      if (!text.trim()) {
        return;
      }

      // Unlike the Flash socket, the dialogue API takes no context_id, so closing the socket is the only way to cancel a reply the server is voicing.
      // A reply with no text sent yet has nothing to cancel, so the new item keeps that socket, even mid-connect.
      if (turn && turn.hasSentText && !turn.isAudioDone && ws) {
        connectionLog?.recordCloseRequested("destroy");
        const interrupted = ws;
        ws = null;
        connectionState = "disconnected";
        connectingPromise = null;
        interrupted.close();
      }

      // Wake the previous turn's generator so it sees it was replaced and ends.
      queueResolve?.();
      _resetQueue();
      const activeTurn: Turn = {
        contextId,
        unsentText: "",
        isTextComplete: false,
        isSocketReady: false,
        hasSentText: false,
        isFlushed: false,
        isAudioDone: false,
        receivedSamples: 0,
        lastTextAt: Date.now(),
      };
      turn = activeTurn;
      currentOnTimestamps = onTimestamps ?? null;
      _appendText(activeTurn, text, !maybeHasMore);

      await connect();
      if (turn !== activeTurn) return;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        throw new Error("WebSocket not connected");
      }
      activeTurn.isSocketReady = true;
      _sendReleasableText();
      _ensureKeepAlive(activeTurn);

      while (true) {
        if (turn !== activeTurn) break;
        if (audioQueue.length > 0) {
          yield audioQueue.shift()!;
        } else if (streamEnded) {
          break;
        } else {
          await new Promise<void>((resolve) => {
            queueResolve = resolve;
          });
        }
      }
    }

    return generate();
  };

  const destroy = (options?: { closeRequest: RealtimeCloseRequest }) => {
    destroyed = true;
    connectionLog?.recordCloseRequested(options?.closeRequest ?? "destroy");
    _stopKeepAlive();
    if (ws) {
      try {
        ws.close();
      } catch {
        // already closed
      }
      ws = null;
    }
    turn = null;
    currentOnTimestamps = null;
    connectionState = "disconnected";
    connectingPromise = null;
    _signalEnd();
  };

  return {
    connect,
    stream,
    destroy,
  };
};
